import React, { useRef, useState, useEffect } from "react";
import LiveImage from "../../../Imager/LiveImage";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { getOneImagerCapabilities } from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import ImagerUtil from "../../../../Utility/ImagerUtil";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import ImagerApi from "../../../../api/ImagerApi";
import CaptureProfileUtil from "../../../../Utility/CaptureProfileUtil";
import { PixelSizeCalibration, ImagerCalibration } from "../../../../Model/ImagerCalibration";
import { useCameraCalibrationContext } from "./CameraCalibrationContext";

// const calibrationCard = [
//   "Welcome to Pixel Size Calibration instruction card, please select NEXT to begin calibration",
//   "Put the resolution target on the stage, and align the target as best as possible (i.e. the horizontal and vertical grid lines of the resolution target are nearly horizontal and vertical in the image).(01/05)",
//   "Activate the camera and set it to binning 1 and zoom 1x (or the smallest available zoom value), then select a grid that will fill up the whole image frame the best. (02/05)",
//   'Enter the selected grid size in the "Grid Size" text box. (03/05)',
//   "Focus the resolution target at the highest zoom. (04/05)",
//   'Click the "Calibrate" button and do manual calibration by drawing a line to the image. (05/05)',
//   "Repeat the above steps for each camera.",
// ];

function getLineLength(
  startX: number,
  startY: number,
  endX: number,
  endY: number
): number {
  const XSquare = Math.abs(endX - startX) * Math.abs(endX - startX);
  const YSquare = Math.abs(endY - startY) * Math.abs(endY - startY);

  return Math.sqrt(XSquare + YSquare);
}

// function getPixelSize(pixelSizeCalibration: PixelSizeCalibration) {
//   return (
//     pixelSizeCalibration.lengthInMicron / pixelSizeCalibration.lengthInPixel
//   );
// }

type Props = {
  imagerIdentifier: string;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
  showDrawLine: boolean;
};

export default function PixelSizeContent ({ imagerIdentifier, setIsCalibrationUpdated, showDrawLine }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const background = useRef<HTMLDivElement>(null);
  const [ctx, setCtx] = useState(null as CanvasRenderingContext2D | null);
  const [boundingRect, setBoundingRect] = useState(null as DOMRect | null);

  const cameraCalibrationContext = useCameraCalibrationContext();
  const gridSize = cameraCalibrationContext.pixelSize.gridSize;
  const numberOfSquares = cameraCalibrationContext.pixelSize.numberOfSquares;
  const setLineLength = cameraCalibrationContext.pixelSize.setLineLength;
  const setPixelSize = cameraCalibrationContext.pixelSize.setPixelSizeValue;
  const lightPath = cameraCalibrationContext.lightpath;

  const [imageSrc, setImageSrc] = useState("");

  // const imagerCapabilities = useTypedSelector((state) =>
  //   getOneImagerCapabilities(state, imagerIdentifier)
  // );
  // const [lightPath, setLightPath] = useState(
  //   imagerCapabilities.lightpathCollection.allIds[0]
  // );

  // const [pixelSizeCalibration, setPixelSizeCalibration] = useState(
  //   ImagerUtil.getPixelSizeCalibration(imagerCalibration, lightPath)
  // );
  // const [pixelSize, setPixelSize] = useState(
  //   getPixelSize(pixelSizeCalibration)
  // );

  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const imagerState = moduleState.imager;
  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  useEffect(() => {
    ImagerApi.lastCapturedImage(imagerIdentifier, lightPath).then((imageSrc) =>
      setImageSrc(imageSrc)
    );
  }, [imagerIdentifier, imagerState.freerunCapture, lightPath]);

  let down = false;
  let startX = 0;
  let startY = 0;
  let endX = 0;
  let endY = 0;

  useEffect(() => {
    if (container?.current && canvas?.current && background?.current) {
      canvas.current.width = container.current.clientWidth - 20;
      canvas.current.height = container.current.clientHeight - 20;
      background.current.style.width = container.current.clientWidth + "px";
      background.current.style.height = container.current.clientHeight + "px";
      const canvasCtx = canvas.current.getContext("2d");
      if (canvasCtx) {
        const boundingRect = canvas.current.getBoundingClientRect();
        canvasCtx.lineWidth = 3;
        setCtx(canvasCtx);
        setBoundingRect(boundingRect);
      }
    }

   if(!showDrawLine) {
      if (canvas?.current && ctx) {
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
    }
   }
    
  }, [ctx, imagerState.freerunCapture, showDrawLine]);// to re-render the component everytime cancel or apply triggered

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    down = true;
    if (boundingRect) {
      startX = e.clientX - boundingRect.left;
      startY = e.clientY - boundingRect.top;
    }
  };

  const onMouseUp = () => {
    down = false;
    const zoom = CaptureProfileUtil.getTotalZoom(
      imagerState.captureProfile,
      lightPath
    );
    const binning = CaptureProfileUtil.getBinning(
      imagerState.captureProfile,
      lightPath
    );
    const lineLength = getLineLength(startX, startY, endX, endY);
    const lengthInMicron = gridSize * numberOfSquares;
    const lengthInPixel = (lineLength * binning) / zoom;
    if(lineLength > 0){
      console.log("PIXEL_SIZE", { lengthInMicron }, { lengthInPixel });
      setLineLength(lineLength);
      setPixelSize(lengthInMicron / lengthInPixel);
      setIsCalibrationUpdated(true);
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if (!down) {
      return;
    }
    if (canvas?.current && boundingRect && ctx) {
      endX = e.clientX - boundingRect.left;
      endY = e.clientY - boundingRect.top;
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = startY === endY ? "green" : "red";
      ctx.stroke();
    }
  };

  const onMouseRightClicked = (
    e: React.MouseEvent<HTMLCanvasElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (canvas?.current && ctx) {
      ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);
      setLineLength(-1);
      setIsCalibrationUpdated(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        position: "relative",
      }}
    >
      {!imagerState.freerunCapture && (
        <div
          ref={container}
          style={{
            width: "100%",
            height: "100%",
            display: "relative",
            background: "gray",
          }}
        >
          <div
            ref={background}
            style={{
              position: "absolute",
              overflow: "auto",
              top: 0,
              left: 0,
            }}
          >
            {/* <img src={`${imageSrc}?${new Date().getTime()}`} /> */}
            <img src={imageSrc} alt="" />
          </div>
          <canvas
            id="CANVAS"
            ref={canvas}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            onContextMenu={onMouseRightClicked}
            style={{
              position: "absolute",
              top: "0px",
              left: "0px",
              bottom: "0px",
              right: "0px",
              zIndex: 10,
            }}
          />
        </div>
      )}

      {imagerState.freerunCapture && (
        <LiveImage imagerIdentifier={imagerIdentifier} />
      )}
    </div>
  );
}
